// Single source of truth for the Affiliate Value Rebate System.
// Hidden from non-admin users for now.

export type RebateTier = "silver" | "gold" | "platinum";

export interface ScreenReward {
  screens: number;
  durationWeeks: number;
}

export interface DiscountConfig {
  // Discount accumulates +stepPercent for every `everySalesStep` SAR past the silver threshold.
  // Capped per tier in `capByTier`.
  stepPercent: number;
  everySalesStep: number;
  capByTier: Record<RebateTier, number>;
}

export interface MediaOwnerRebate {
  name: string;
  thresholds: Record<RebateTier, number>;
  screenRewards: Record<RebateTier, ScreenReward>;
  discount: DiscountConfig | null;
  /** Commission rate when affiliate has not yet hit Silver for this owner. */
  baseCommissionPercent: number;
  /** Commission rate at each qualified tier. */
  commissionByTier: Record<RebateTier, number>;
}

const STANDARD_THRESHOLDS: Record<RebateTier, number> = {
  silver: 250_000,
  gold: 500_000,
  platinum: 750_000,
};

const ONSPOT_THRESHOLDS: Record<RebateTier, number> = {
  silver: 100_000,
  gold: 200_000,
  platinum: 300_000,
};

export const REBATE_MEDIA_OWNERS: MediaOwnerRebate[] = [
  {
    name: "Alan",
    thresholds: STANDARD_THRESHOLDS,
    screenRewards: {
      silver: { screens: 60, durationWeeks: 2 },
      gold: { screens: 120, durationWeeks: 2 },
      platinum: { screens: 180, durationWeeks: 2 },
    },
    discount: {
      stepPercent: 5,
      everySalesStep: 50_000,
      capByTier: { silver: 10, gold: 15, platinum: 25 },
    },
    baseCommissionPercent: 4,
    commissionByTier: { silver: 8, gold: 12, platinum: 16 },
  },
  {
    name: "Attention",
    thresholds: STANDARD_THRESHOLDS,
    screenRewards: {
      silver: { screens: 2, durationWeeks: 2 },
      gold: { screens: 4, durationWeeks: 2 },
      platinum: { screens: 6, durationWeeks: 2 },
    },
    discount: null, // TBD
    baseCommissionPercent: 2.5,
    commissionByTier: { silver: 3, gold: 3.5, platinum: 4 },
  },
  {
    name: "On Spot",
    thresholds: ONSPOT_THRESHOLDS,
    screenRewards: {
      silver: { screens: 10, durationWeeks: 2 },
      gold: { screens: 20, durationWeeks: 2 },
      platinum: { screens: 30, durationWeeks: 2 },
    },
    discount: null, // TBD
    baseCommissionPercent: 4,
    commissionByTier: { silver: 6, gold: 8, platinum: 10 },
  },
];

export const TIER_ORDER: RebateTier[] = ["silver", "gold", "platinum"];

export const TIER_LABEL: Record<RebateTier, { en: string; ar: string }> = {
  silver: { en: "Silver", ar: "فضي" },
  gold: { en: "Gold", ar: "ذهبي" },
  platinum: { en: "Platinum", ar: "بلاتيني" },
};

export const TIER_ACCENT: Record<RebateTier, { from: string; to: string; ring: string; text: string }> = {
  silver: { from: "from-slate-400/20", to: "to-slate-300/10", ring: "ring-slate-400/40", text: "text-slate-500" },
  gold: { from: "from-amber-400/25", to: "to-yellow-300/10", ring: "ring-amber-400/50", text: "text-amber-500" },
  platinum: { from: "from-violet-500/25", to: "to-fuchsia-400/10", ring: "ring-violet-500/50", text: "text-violet-500" },
};

/**
 * Determine the highest tier an affiliate qualifies for at a given media owner
 * based on their quarterly sales total.
 */
export function computeTierForOwner(salesSar: number, owner: MediaOwnerRebate): RebateTier | null {
  if (salesSar >= owner.thresholds.platinum) return "platinum";
  if (salesSar >= owner.thresholds.gold) return "gold";
  if (salesSar >= owner.thresholds.silver) return "silver";
  return null;
}

/**
 * Compute the highest overall tier across all media owners using STANDARD thresholds
 * (used for the affiliate-level "auto tier" badge in the admin table).
 */
export function computeOverallAutoTier(salesSar: number): RebateTier | null {
  if (salesSar >= STANDARD_THRESHOLDS.platinum) return "platinum";
  if (salesSar >= STANDARD_THRESHOLDS.gold) return "gold";
  if (salesSar >= STANDARD_THRESHOLDS.silver) return "silver";
  return null;
}

/**
 * Compute the discount percentage earned at a given owner for a given sales total + tier.
 */
export function computeDiscountPercent(
  salesSar: number,
  tier: RebateTier,
  owner: MediaOwnerRebate
): number {
  if (!owner.discount) return 0;
  const cap = owner.discount.capByTier[tier];
  const overSilver = Math.max(0, salesSar - owner.thresholds.silver);
  const steps = Math.floor(overSilver / owner.discount.everySalesStep);
  const earned = steps * owner.discount.stepPercent;
  return Math.min(cap, earned);
}

/**
 * Get the affiliate sale-commission percentage for a given media owner & tier.
 * When tier is null, the affiliate has not yet hit Silver — return the base rate.
 */
export function getCommissionPercent(
  owner: MediaOwnerRebate,
  tier: RebateTier | null
): number {
  if (!tier) return owner.baseCommissionPercent;
  return owner.commissionByTier[tier];
}
